"use client";

import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { createFrontendClient, PipedreamEnvironment } from "@pipedream/sdk/browser";
import { useCallback, useRef, useState } from "react";

// Shared external user ID for all Pipedream operations.
// This keeps Pipedream costs flat ($150/mo) regardless of user count.
// Account isolation is maintained via unique accountIds stored per-connector in our DB.
export const SHARED_EXTERNAL_USER_ID = "anyspend";

// Custom OAuth app IDs for B3OS branding on OAuth consent screens.
// When set, users see "B3OS wants access..." instead of "Pipedream wants access..."
//
// HOW TO ADD A NEW PROVIDER:
// 1. Create an OAuth App on the provider's developer portal (e.g., api.slack.com, console.cloud.google.com)
//    - Set the app name to "B3OS" and upload B3OS logo
//    - Add required scopes (check Pipedream docs for each app)
//    - Add redirect URI: https://api.pipedream.com/connect/oauth/oa_XXXXX/callback
// 2. On Pipedream (pipedream.com/@/accounts/oauth-clients), create a Custom OAuth Client
//    - Select the app, paste Client ID + Secret from step 1
//    - Note the generated oauthAppId (e.g., "oa_XXXXX")
// 3. Update the redirect URI in step 1 with the actual oauthAppId from step 2
// 4. Add the mapping below: { [appSlug]: "oa_XXXXX" }
//    - If the app has multiple slugs, add both
//
// See: services/b3os-workflow/docs/PIPEDREAM_INTEGRATION.md → "Custom Branding" section
const OAUTH_APP_IDS: Record<string, string> = {
  slack: "oa_oRiRWw",
  ...(process.env.NEXT_PUBLIC_PIPEDREAM_DISCORD_OAUTH_APP_ID && {
    discord: process.env.NEXT_PUBLIC_PIPEDREAM_DISCORD_OAUTH_APP_ID
  })
};
type FrontendClient = ReturnType<typeof createFrontendClient>;
export interface ConnectAccountResult {
  accountId: string;
  appSlug: string;
}
interface ConnectAccountOptions {
  appSlug: string;
  /** If true, resolve only after user closes the modal (clicks "Continue") */
  waitForClose?: boolean;
}
interface UsePipedreamClientResult {
  /** Whether a connection is in progress */
  isConnecting: boolean;
  /** Error message if connection failed */
  error: string | null;
  /** Clear the error state */
  clearError: () => void;
  /** Connect an account via OAuth/keys flow */
  connectAccount: (options: ConnectAccountOptions) => Promise<ConnectAccountResult | null>;
}

/**
 * Hook for Pipedream client management and OAuth flow.
 * Handles token fetching, client initialization, and account connection.
 */
export function usePipedreamClient(): UsePipedreamClientResult {
  const frontendClientRef = useRef<FrontendClient | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Token callback for Pipedream SDK - called whenever SDK needs a new token
  const tokenCallback = useCallback(async () => {
    const response = await authenticatedFetch<{
      code: number;
      data: {
        token: string;
        expires_at: string;
      };
    }>(`${V2_ENDPOINT}/v1/pipedream/connect-token`);
    if (!response.ok) {
      throw new Error("Failed to fetch connect token");
    }
    const data = await response.json();
    if (data.code !== 0) {
      throw new Error("Failed to fetch connect token");
    }
    return {
      token: data.data.token,
      expiresAt: new Date(data.data.expires_at),
      connectLinkUrl: ""
    };
  }, []);

  // Get or create frontend client with shared external user ID
  const getClient = useCallback(async (): Promise<FrontendClient> => {
    if (frontendClientRef.current) {
      return frontendClientRef.current;
    }
    const initialToken = await tokenCallback();
    const client = createFrontendClient({
      environment: "production" as PipedreamEnvironment,
      tokenCallback,
      externalUserId: SHARED_EXTERNAL_USER_ID,
      token: initialToken.token
    });
    frontendClientRef.current = client;
    return client;
  }, [tokenCallback]);
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Connect an account via OAuth/keys flow
  const connectAccount = useCallback(async (options: ConnectAccountOptions): Promise<ConnectAccountResult | null> => {
    const {
      appSlug,
      waitForClose = false
    } = options;
    setIsConnecting(true);
    setError(null);
    try {
      const client_0 = await getClient();
      const result = await new Promise<{
        id: string;
      }>((resolve, reject) => {
        let accountId: string | null = null;
        client_0.connectAccount({
          app: appSlug,
          ...(OAUTH_APP_IDS[appSlug] && {
            oauthAppId: OAUTH_APP_IDS[appSlug]
          }),
          onSuccess: (res: {
            id: string;
          }) => {
            accountId = res.id;
            // If not waiting for close, resolve immediately
            if (!waitForClose) {
              resolve({
                id: res.id
              });
            }
          },
          onError: (err_0: Error) => {
            reject(err_0);
          },
          onClose: (status: {
            successful: boolean;
            completed: boolean;
          }) => {
            if (waitForClose) {
              // Resolve only after modal closes
              if (status.successful && accountId) {
                resolve({
                  id: accountId
                });
              } else {
                reject(new Error("Connection cancelled"));
              }
            } else if (!status.successful && !accountId) {
              // User closed without connecting
              reject(new Error("Connection window was closed"));
            }
          }
        });
      });
      return {
        accountId: result.id,
        appSlug
      };
    } catch (err) {
      console.error("Pipedream connect error:", err);
      if (err instanceof Error) {
        if (err.message === "Connection cancelled") {
          // User cancelled, no error message needed
          return null;
        } else if (err.message.includes("closed")) {
          setError("Connection window was closed. Please try again.");
        } else {
          setError("Failed to connect account. Please try again.");
        }
      } else {
        setError("Failed to connect account. Please try again.");
      }
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, [getClient]);
  return {
    isConnecting,
    error,
    clearError,
    connectAccount
  };
}